// ─── FleetQuickPanel ────────────────────────────────────────────────
// Painel flutuante no canto superior esquerdo do mapa (Planeamento), logo
// por baixo da barra de pesquisa. Lista rápida das viaturas do utilizador
// para acesso IMEDIATO à localização de qualquer uma: clicar numa linha
// centra o mapa nela (reutiliza o evento `focusVehicle` que o mapa já trata
// — NÃO redefine o clicar). Estilo espelhado no TransitMonitor ("Em
// trânsito"): cabeçalho escuro, lista branca, arrastável e com pill quando
// recolhido. ARRANCA RECOLHIDO por defeito. Posição/estado em localStorage.
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Tooltip, InputBase } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import PersonIcon from '@material-ui/icons/Person';
import RoomIcon from '@material-ui/icons/Room';
import SpeedIcon from '@material-ui/icons/Speed';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import RemoveIcon from '@material-ui/icons/Remove';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import Battery60Icon from '@material-ui/icons/Battery60';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { vehicleIconCategory, getVehicleIconDataUrl, onVehicleIconLoaded } from '../../utils/vehicleIcons';

const POS_KEY = 'fleetQuickPanelPos';
const COLLAPSED_KEY = 'fleetQuickPanelCollapsed';
const NO_SIGNAL_MIN = 20; // sem fix há ≥20 min → cinzento "sem sinal"

const fmtAge = (min) => {
  if (min == null || !Number.isFinite(min)) return null;
  if (min < 1) return 'agora';
  if (min < 60) return `${min}m`;
  if (min < 1440) return `${Math.round(min / 60)}h`;
  return `${Math.round(min / 1440)}d`;
};

// Estado da viatura → cor + etiqueta. Igual à lógica da popup do veículo
// (verde=movimento, âmbar=ralenti, vermelho=parado, cinza=sem sinal).
function classify(v) {
  const pos = v.position || {};
  const attr = pos.attributes || {};
  const speed = Number(pos.speed) || 0;
  const moving = speed > 3;
  const fixMs = (pos.fixTime || pos.deviceTime) ? Date.now() - new Date(pos.fixTime || pos.deviceTime).getTime() : null;
  const fixAge = fixMs != null ? Math.round(fixMs / 60000) : null;
  if (moving) return { key: 'moving', label: 'Em condução', dot: '#16a34a', color: '#15803d', bg: '#f0fdf4', icon: 'green', sev: 3, speed };
  if (!moving && fixAge != null && fixAge >= NO_SIGNAL_MIN && !attr.ignition) {
    return { key: 'nosignal', label: 'Sem sinal', dot: '#94a3b8', color: '#475569', bg: '#f1f5f9', icon: 'gray', sev: 0, speed };
  }
  if (attr.ignition) return { key: 'idle', label: 'Ralenti', dot: '#f59e0b', color: '#b45309', bg: '#fffbeb', icon: 'yellow', sev: 2, speed };
  return { key: 'stopped', label: 'Estacionado', dot: '#ef4444', color: '#b91c1c', bg: '#fef2f2', icon: 'red', sev: 1, speed };
}

const hasValidPos = (v) => {
  const p = v.position; if (!p) return false;
  const lat = p.latitude, lng = p.longitude;
  if (typeof lat !== 'number' || typeof lng !== 'number') return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
};

// Miniatura do ícone da viatura (mesmo conjunto do mapa), colorido por estado.
// Re-renderiza quando o PNG acaba de carregar (data-URI cacheado).
function VehicleMini({ type, color }) {
  const [, force] = useState(0);
  useEffect(() => onVehicleIconLoaded(() => force(n => n + 1)), []);
  const cat = vehicleIconCategory(type);
  const url = getVehicleIconDataUrl(cat, color) || getVehicleIconDataUrl(cat, 'gray');
  return (
    <Box style={{ width: 38, height: 38, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10 }}>
      {url
        ? <img src={url} alt="" style={{ maxWidth: 32, maxHeight: 32, objectFit: 'contain' }} />
        : <DirectionsCarIcon style={{ fontSize: 22, color: '#94a3b8' }} />}
    </Box>
  );
}

const useStyles = makeStyles(() => ({
  '@global': {
    '@keyframes fqpLive': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
  },
  panel: {
    position: 'fixed', zIndex: 620, width: 344, maxWidth: 'calc(100vw - 24px)',
    background: '#fff', borderRadius: 14, overflow: 'hidden',
    boxShadow: '0 12px 40px rgba(15,23,42,0.22), 0 2px 8px rgba(15,23,42,0.10)',
    border: '1px solid rgba(15,23,42,0.08)',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 9, padding: '11px 12px',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: '#fff',
    userSelect: 'none',
  },
  headerIcon: { width: 26, height: 26, borderRadius: 8, background: 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: '0.82rem', fontWeight: 800 },
  count: { fontSize: '0.66rem', fontWeight: 800, background: '#22c55e', color: '#052e16', borderRadius: 999, padding: '1px 8px', minWidth: 18, textAlign: 'center' },
  headBtn: { color: 'rgba(255,255,255,0.7)', cursor: 'pointer', display: 'flex', padding: 4, borderRadius: 6, '&:hover': { background: 'rgba(255,255,255,0.16)', color: '#fff' } },
  searchRow: {
    display: 'flex', alignItems: 'center', gap: 7, padding: '8px 10px 6px',
    background: '#fbfcfe',
  },
  searchBox: {
    display: 'flex', alignItems: 'center', gap: 6, flex: 1,
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 9, padding: '4px 9px',
    transition: 'border-color 0.12s, box-shadow 0.12s',
    '&:focus-within': { borderColor: '#94a3b8', boxShadow: '0 0 0 3px rgba(148,163,184,0.15)' },
  },
  input: { flex: 1, fontSize: '0.76rem', color: '#0f172a' },
  statChips: { display: 'flex', alignItems: 'center', gap: 6, padding: '2px 12px 9px', flexWrap: 'wrap', borderBottom: '1px solid #eef2f7', background: '#fbfcfe' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.62rem', fontWeight: 800, color: '#475569', background: '#fff', border: '1px solid #eef2f7', borderRadius: 999, padding: '3px 9px' },
  chipDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  list: { maxHeight: '56vh', overflowY: 'auto', padding: '8px 8px 4px', background: '#f4f6f9' },
  row: {
    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 11px',
    borderRadius: 12, cursor: 'pointer', marginBottom: 8,
    background: '#fff', border: '1px solid #e6ebf2', borderLeftWidth: 4,
    boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
    transition: 'transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease',
    '&:hover': { background: '#fafcff', transform: 'translateX(2px)', boxShadow: '0 6px 16px rgba(15,23,42,0.12)' },
  },
  name: { fontSize: '0.79rem', fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  plate: { fontSize: '0.63rem', fontWeight: 700, fontFamily: '"SF Mono","Fira Code",monospace', color: '#94a3b8', flexShrink: 0 },
  sub: { fontSize: '0.68rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 },
  meta: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' },
  metaItem: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.62rem', fontWeight: 700, color: '#64748b' },
  badge: { fontSize: '0.57rem', fontWeight: 800, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' },
  spd: { fontSize: '0.62rem', fontWeight: 800, color: '#15803d', marginTop: 3, whiteSpace: 'nowrap', textAlign: 'right', display: 'inline-flex', alignItems: 'center', gap: 3, justifyContent: 'flex-end' },
  empty: { padding: '22px 14px', textAlign: 'center', color: '#94a3b8', fontSize: '0.76rem' },
  footer: { padding: '7px 12px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', background: '#fbfcfe' },
  liveDot: { width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: '$fqpLive 1.4s ease-in-out infinite' },
  pill: {
    position: 'fixed', zIndex: 650, cursor: 'pointer', userSelect: 'none',
    display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px 3px 8px',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: '#fff',
    borderRadius: 999, boxShadow: '0 4px 14px rgba(15,23,42,0.32)',
    border: '1px solid rgba(255,255,255,0.08)',
    fontSize: '0.66rem', fontWeight: 700, lineHeight: 1.2,
    transition: 'transform 0.15s, box-shadow 0.15s',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(15,23,42,0.42)' },
  },
  pillCount: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.58rem', fontWeight: 800, padding: '0 6px', height: 14,
    borderRadius: 8, minWidth: 18, background: '#22c55e', color: '#052e16',
  },
}));

function FleetQuickPanel({ lang = 'pt' }) {
  const classes = useStyles();
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const vehicles = useSelector(selectVehicles);

  // ARRANCA RECOLHIDO por defeito (só fica aberto se o utilizador o abriu antes).
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(COLLAPSED_KEY) !== '0'; } catch { return true; } });
  const [query, setQuery] = useState('');
  // Posição ancorada ao TOPO {left, top} — arranca por baixo da barra de
  // pesquisa (top:62 + ~44 de altura + margem).
  const [pos, setPos] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(POS_KEY)); if (s && Number.isFinite(s.left) && Number.isFinite(s.top)) return s; } catch {}
    return { left: 12, top: 114 };
  });
  const drag = useRef(null);

  const persistCollapsed = (val) => { setCollapsed(val); try { localStorage.setItem(COLLAPSED_KEY, val ? '1' : '0'); } catch {} };

  // ── Drag (ancorado ao topo: cresce para baixo) ──
  const onMove = useCallback((e) => {
    if (!drag.current) return;
    const left = Math.max(6, Math.min(window.innerWidth - 120, drag.current.startLeft + (e.clientX - drag.current.mx)));
    const top = Math.max(6, Math.min(window.innerHeight - 60, drag.current.startTop + (e.clientY - drag.current.my)));
    setPos({ left, top });
  }, []);
  const onUp = useCallback(() => {
    if (drag.current) setPos(p => { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch {} return p; });
    drag.current = null;
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  }, [onMove]);
  const onDown = (e) => {
    drag.current = { mx: e.clientX, my: e.clientY, startLeft: pos.left, startTop: pos.top };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Centra o mapa na viatura — MESMO mecanismo do resto da app (FleetView,
  // TransitMonitor). Não redefine nada do clicar; delega no mapa.
  const focus = (v) => {
    const p = v.position || {};
    window.dispatchEvent(new CustomEvent('focusVehicle', { detail: { id: v.id, lat: p.latitude, lng: p.longitude } }));
  };

  // Linhas: só viaturas com posição válida; ordenadas (em movimento primeiro),
  // filtradas pela pesquisa (matrícula/nome/motorista). Enriquecidas com
  // morada, combustível, tensão e idade do último fix.
  const { rows, moving, idle, stopped, nosignal } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = (vehicles || [])
      .filter(hasValidPos)
      .map(v => {
        const c = classify(v);
        const pos = v.position || {};
        const attr = pos.attributes || {};
        const driver = attr.driverName || v.driverName || v.currentDriver?.name || null;
        const fuelRaw = attr.io89 != null ? attr.io89 : attr.io9;
        const fuel = fuelRaw != null ? `${Math.round(Number(fuelRaw))}%` : null;
        const volt = attr.power != null ? `${Number(attr.power).toFixed(1)}V` : null;
        const fixMs = (pos.fixTime || pos.deviceTime) ? Date.now() - new Date(pos.fixTime || pos.deviceTime).getTime() : null;
        const ageMin = fixMs != null ? Math.round(fixMs / 60000) : null;
        return { v, c, driver, fuel, volt, ageMin, address: pos.address || null };
      });
    const filtered = q
      ? base.filter(({ v, driver }) =>
          String(v.plate || '').toLowerCase().includes(q) ||
          String(v.name || '').toLowerCase().includes(q) ||
          String(driver || '').toLowerCase().includes(q))
      : base;
    filtered.sort((a, b) => (b.c.sev - a.c.sev) || String(a.v.name || a.v.plate || '').localeCompare(String(b.v.name || b.v.plate || '')));
    return {
      rows: filtered,
      moving: base.filter(r => r.c.key === 'moving').length,
      idle: base.filter(r => r.c.key === 'idle').length,
      stopped: base.filter(r => r.c.key === 'stopped').length,
      nosignal: base.filter(r => r.c.key === 'nosignal').length,
    };
  }, [vehicles, query]);

  const total = useMemo(() => (vehicles || []).filter(hasValidPos).length, [vehicles]);

  if (collapsed) {
    return (
      <Box className={classes.pill} style={{ left: pos.left, top: pos.top }} onClick={() => persistCollapsed(false)}>
        <DirectionsCarIcon style={{ fontSize: 13 }} />
        {T('Frota', 'Fleet')}
        <span className={classes.pillCount}>{total}</span>
        <KeyboardArrowDownIcon style={{ fontSize: 13, opacity: 0.6 }} />
      </Box>
    );
  }

  return (
    <Box className={classes.panel} style={{ left: pos.left, top: pos.top }}>
      <Box className={classes.header} style={{ cursor: 'grab' }} onMouseDown={onDown}>
        <Box className={classes.headerIcon}><DirectionsCarIcon style={{ fontSize: 15 }} /></Box>
        <Typography className={classes.title}>{T('Frota', 'Fleet')}</Typography>
        <span className={classes.count}>{total}</span>
        <Box style={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
          <Tooltip title={T('Recolher', 'Collapse')} arrow>
            <span className={classes.headBtn} onClick={() => persistCollapsed(true)}><RemoveIcon style={{ fontSize: 16 }} /></span>
          </Tooltip>
        </Box>
      </Box>

      <Box className={classes.searchRow}>
        <Box className={classes.searchBox}>
          <SearchIcon style={{ fontSize: 15, color: '#94a3b8' }} />
          <InputBase
            className={classes.input}
            placeholder={T('Procurar matrícula, nome ou motorista…', 'Search plate, name or driver…')}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {query && (
            <span style={{ cursor: 'pointer', display: 'flex' }} onClick={() => setQuery('')}>
              <CloseIcon style={{ fontSize: 14, color: '#94a3b8' }} />
            </span>
          )}
        </Box>
      </Box>

      {total > 0 && (
        <Box className={classes.statChips}>
          <span className={classes.chip}><span className={classes.chipDot} style={{ background: '#16a34a' }} />{moving} {T('a andar', 'moving')}</span>
          <span className={classes.chip}><span className={classes.chipDot} style={{ background: '#f59e0b' }} />{idle} {T('ralenti', 'idle')}</span>
          <span className={classes.chip}><span className={classes.chipDot} style={{ background: '#ef4444' }} />{stopped} {T('parados', 'stopped')}</span>
          <span className={classes.chip}><span className={classes.chipDot} style={{ background: '#94a3b8' }} />{nosignal} {T('sem sinal', 'no signal')}</span>
        </Box>
      )}

      {rows.length === 0 ? (
        <Typography className={classes.empty}>
          {total === 0
            ? T('Sem viaturas com localização.', 'No vehicles with a location.')
            : T('Nenhuma viatura corresponde à pesquisa.', 'No vehicle matches the search.')}
        </Typography>
      ) : (
        <Box className={classes.list}>
          {rows.map(({ v, c, driver, fuel, volt, ageMin, address }) => (
            <Box key={v.id} className={classes.row} style={{ borderLeftColor: c.dot }} onClick={() => focus(v)}>
              <VehicleMini type={v.type} color={c.icon} />
              <Box style={{ minWidth: 0, flex: 1 }}>
                <Box style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span className={classes.name}>{v.name || v.plate || '—'}</span>
                  {v.name && v.plate && v.name !== v.plate && <span className={classes.plate}>{v.plate}</span>}
                </Box>
                {driver && (
                  <Typography className={classes.sub}>
                    <PersonIcon style={{ fontSize: 12, color: '#cbd5e1' }} />{driver}
                  </Typography>
                )}
                {address && (
                  <Typography className={classes.sub}>
                    <RoomIcon style={{ fontSize: 12, color: '#cbd5e1' }} />{address}
                  </Typography>
                )}
                <Box className={classes.meta}>
                  {fuel && <span className={classes.metaItem}><LocalGasStationIcon style={{ fontSize: 12, color: '#2563eb' }} />{fuel}</span>}
                  {volt && <span className={classes.metaItem}><Battery60Icon style={{ fontSize: 12, color: '#16a34a' }} />{volt}</span>}
                  {ageMin != null && <span className={classes.metaItem}><AccessTimeIcon style={{ fontSize: 12, color: '#cbd5e1' }} />{fmtAge(ageMin)}</span>}
                </Box>
              </Box>
              <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0, flexShrink: 0 }}>
                <span className={classes.badge} style={{ background: c.bg, color: c.color }}>{c.label}</span>
                {c.key === 'moving' && (
                  <span className={classes.spd}><SpeedIcon style={{ fontSize: 12 }} />{Math.round(c.speed)} km/h</span>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box className={classes.footer}>
        <span className={classes.liveDot} />
        {T('Posições em tempo real · clica para localizar', 'Live positions · click to locate')}
      </Box>
    </Box>
  );
}

export default FleetQuickPanel;
